import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, MessagesSquare, Users, Compass, Sparkles, Truck, CreditCard } from "lucide-react";
import useSEO from "@/hooks/useSEO";
import { track, useTrackPageView } from "@/lib/analytics";
import KineticTitle, { HeroFade } from "@/components/books/KineticTitle";
import Reveal from "@/components/books/Reveal";
import MarqueeRibbon from "@/components/books/MarqueeRibbon";
import BookCover from "@/components/books/BookCover";
import BilingualBadge from "@/components/books/BilingualBadge";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { CHINNU_BOOKS, CHINNU_BUNDLE, CHINNU_FAQ, SERIES, CHINNU_AUTHOR, CHINNU_MANIFESTO } from "@/data/books";

const WHY = [
  { Icon: MessagesSquare, t: "Real conversation practice", d: "Everyday scenarios children rehearse aloud — until the words are theirs." },
  { Icon: Users, t: "Parent-child, side by side", d: "Written to be read together, never alone. You are the co-star." },
  { Icon: Compass, t: "Growing independence", d: "Small choices that quietly build confident little decision-makers." },
  { Icon: Sparkles, t: "Activity on every page", d: "Something to do, point at or act out — not just text to read." },
];

export default function ChinnuSeries() {
  useTrackPageView("chinnu_series_view");
  useSEO({
    title: "Chinnu Talking Adventures — Bilingual English–Telugu Printed Books · LifeIQ Books",
    description: "A bilingual English–Telugu series of 4 activity-based talking books, printed and shipped across India — real conversations, real practice, one book at a time.",
    canonicalPath: "/books/chinnu",
  });

  const series = SERIES.find((s) => s.id === "chinnu");

  return (
    <main>
      {/* SERIES HERO — bilingual, kinetic, Telugu watermark */}
      <section className="relative bg-[#0D1B2A] text-white overflow-hidden">
        <span
          aria-hidden="true"
          className="font-telugu absolute -bottom-16 -left-4 text-[24vw] leading-none text-outline-cream opacity-50 pointer-events-none select-none"
        >
          చిన్ని
        </span>
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 pt-20 md:pt-28 pb-24 md:pb-32 relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <HeroFade delay={0}>
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="text-[12px] uppercase tracking-[0.24em] text-[#F57F17] font-semibold">A LifeIQ Books Series</span>
                  <span className="text-white/30">·</span>
                  <span className="text-[12px] uppercase tracking-[0.24em] text-white/60 font-semibold">
                    English + <span className="font-telugu normal-case text-[13px]">తెలుగు</span>
                  </span>
                </div>
              </HeroFade>
              <KineticTitle
                testId="chinnu-series-hero-title"
                className="mt-6 font-serif-d text-[42px] sm:text-6xl lg:text-[68px] leading-[1.04] tracking-tight text-white"
                lines={["Chinnu Talking", "Adventures"]}
              />
              <HeroFade delay={0.28}>
                <div data-testid="chinnu-bilingual-strip" className="mt-5 flex flex-wrap items-center gap-3">
                  <BilingualBadge dark testId="chinnu-series-bilingual-badge" />
                  <span data-testid="chinnu-series-bilingual-headline" className="text-[13px] sm:text-[15px] uppercase tracking-[0.14em] font-bold text-white/90">
                    4 Interactive Bilingual English–Telugu Books
                  </span>
                </div>
              </HeroFade>
              <HeroFade delay={0.4}>
                <p className="mt-5 font-telugu text-[24px] sm:text-[28px] text-[#F5B25C] leading-[1.3]">{series.name_telugu}</p>
                <p className="mt-4 font-serif-d italic text-[19px] sm:text-[21px] text-white/70">{series.tagline}</p>
                <p className="mt-5 text-[16px] sm:text-[17px] text-white/80 leading-[1.7] max-w-2xl">{series.description}</p>
                <div className="mt-10 flex flex-col sm:flex-row gap-3">
                  <a href="#books" data-testid="chinnu-explore" className="inline-flex items-center justify-center gap-2 bg-[#F57F17] text-white px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-[#D86B0F] transition-colors duration-200 rounded-sm">
                    Explore the 4 Books <ArrowRight size={15} />
                  </a>
                  <Link
                    to="/books/chinnu/complete-collection"
                    data-testid="chinnu-bundle-quick-cta"
                    onClick={() => track("bundle_view", { source: "series-hero" })}
                    className="inline-flex items-center justify-center gap-2 bg-transparent text-white border border-white/40 px-7 py-4 text-[13px] font-semibold tracking-wide uppercase hover:bg-white/10 transition-colors duration-200 rounded-sm"
                  >
                    Bundle — {CHINNU_BUNDLE.print_price}
                  </Link>
                </div>
              </HeroFade>
            </div>
            <div className="lg:col-span-5">
              <HeroFade delay={0.45} className="grid grid-cols-2 gap-4 max-w-[440px] mx-auto">
                {CHINNU_BOOKS.map((b, i) => (
                  <div key={b.slug} style={{ transform: `translateY(${i % 2 ? "14px" : "0"})` }}>
                    <BookCover book={b} tilt testId={`chinnu-hero-cover-${b.slug}`} />
                  </div>
                ))}
              </HeroFade>
            </div>
          </div>
        </div>
      </section>

      <MarqueeRibbon tone="cream" />

      {/* WHY CHINNU */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] text-[#F57F17] font-semibold">Why Chinnu</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[48px] leading-[1.08] text-[#0D1B2A] max-w-3xl">Small books that grow big habits.</h2>
            <p className="mt-6 text-[16px] sm:text-[18px] text-[#1A1A1A] leading-[1.7] max-w-3xl">
              Chinnu is not a reading book. It is a talking book — designed to be worked through together, one gentle conversation at a time. Each page invites practice, not performance.
            </p>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {WHY.map(({ Icon, t, d }, i) => (
              <Reveal key={t} delay={i * 0.07}>
                <div className="bg-[#FBF7EE] border border-[#E7DFCF] rounded-sm p-6 h-full hover:border-[#F57F17]/50 transition-colors duration-200">
                  <div className="w-11 h-11 rounded-full bg-[#F57F17] text-white flex items-center justify-center"><Icon size={20} /></div>
                  <div className="mt-5 font-serif-d text-[19px] text-[#0D1B2A] leading-tight">{t}</div>
                  <p className="mt-2 text-[14px] text-[#5C6470] leading-[1.65]">{d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* THE CHINNU PHILOSOPHY — numbered editorial chapters */}
      <section className="bg-[#FBF7EE] border-t border-[#E7DFCF]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] text-[#F57F17] font-semibold">The Chinnu philosophy</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[48px] leading-[1.08] text-[#0D1B2A] max-w-3xl">
              Why we make books that talk back.
            </h2>
          </Reveal>
          <div className="mt-16">
            {CHINNU_MANIFESTO.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div data-testid={`chinnu-manifesto-chapter-${i + 1}`} className="grid grid-cols-12 gap-6 py-10 border-t border-[#E7DFCF] items-start">
                  <div className="col-span-3 sm:col-span-2 font-serif-d text-[40px] sm:text-[56px] leading-none text-[#F57F17]">{c.n}</div>
                  <div className="col-span-9 sm:col-span-4 font-serif-d text-[22px] sm:text-[26px] leading-[1.2] text-[#0D1B2A]">{c.title}</div>
                  <p className="col-span-12 sm:col-span-6 text-[15px] sm:text-[16px] text-[#5C6470] leading-[1.75]">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT THE AUTHOR */}
      <section className="bg-white border-t border-[#E7DFCF]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-20 md:py-24">
          <Reveal>
            <div data-testid="chinnu-author-block" className="flex flex-col sm:flex-row gap-6 sm:gap-10 items-start bg-[#FBF7EE] border border-[#E7DFCF] rounded-sm p-8 sm:p-10">
              <img
                src={CHINNU_AUTHOR.photo}
                alt={CHINNU_AUTHOR.photo_alt}
                data-testid="chinnu-author-photo"
                className="w-24 h-24 sm:w-28 sm:h-28 rounded-full object-cover object-top shrink-0 border-2 border-[#F5B25C]/60"
              />
              <div>
                <div className="text-[12px] uppercase tracking-[0.22em] text-[#F57F17] font-semibold">About the author</div>
                <h3 data-testid="chinnu-author-name" className="mt-2 font-serif-d text-[24px] sm:text-[28px] text-[#0D1B2A]">{CHINNU_AUTHOR.name}</h3>
                <p className="mt-3 text-[15px] sm:text-[16px] text-[#1A1A1A] leading-[1.7] max-w-2xl">{CHINNU_AUTHOR.bio}</p>
                <div className="mt-4 text-[12px] uppercase tracking-[0.18em] text-[#004D40] font-semibold">{CHINNU_AUTHOR.role}</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* THE 4-BOOK JOURNEY — numbered chapters */}
      <section id="books" className="bg-[#FBF7EE]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] text-[#F57F17] font-semibold">The books</div>
            <h2 className="mt-5 font-serif-d text-[34px] sm:text-5xl lg:text-[48px] leading-[1.08] text-[#0D1B2A]">Four books. One growing voice.</h2>
          </Reveal>

          <div className="mt-16">
            {CHINNU_BOOKS.map((b, i) => (
              <Reveal key={b.slug} delay={0.05}>
                <div data-testid={`journey-book-${b.book_number}`} className="grid grid-cols-12 gap-6 md:gap-10 py-10 border-t border-[#E7DFCF] items-center">
                  <div className="col-span-12 sm:col-span-3 md:col-span-2">
                    <Link to={`/books/chinnu/${b.slug}`} aria-label={`View ${b.title}`}>
                      <BookCover book={b} tilt testId={`journey-cover-${b.slug}`} className="max-w-[180px]" />
                    </Link>
                  </div>
                  <div className="col-span-12 sm:col-span-9 md:col-span-7">
                    <div className="text-[11px] uppercase tracking-[0.24em] text-[#F57F17] font-semibold">Book {b.book_number}</div>
                    <h3 className="mt-2 font-serif-d text-[26px] sm:text-[30px] text-[#0D1B2A] leading-[1.12]">
                      {b.title} <span className="font-telugu text-[0.72em] text-[#D86B0F] ml-2 whitespace-nowrap">{b.title_telugu}</span>
                    </h3>
                    <div className="mt-2.5 flex flex-wrap items-center gap-2">
                      <BilingualBadge compact testId={`journey-bilingual-badge-${b.slug}`} />
                      <span className="text-[11px] uppercase tracking-[0.12em] font-bold text-[#004D40]">Bilingual English–Telugu · Printed Book</span>
                    </div>
                    <p className="mt-2 font-serif-d italic text-[16px] text-[#5C6470]">{b.subtitle}</p>
                    <div className="mt-3 inline-flex items-center gap-2 text-[13px] text-[#004D40] font-semibold bg-[#004D40]/[0.06] px-3 py-1.5 rounded-sm">
                      <span className="text-[#F57F17]">◆</span> “{b.series_ladder}”
                    </div>
                    <p className="mt-3 text-[15px] text-[#1A1A1A] leading-[1.65] max-w-xl">{b.short_description}</p>
                    <div className="mt-3 text-[14px] text-[#004D40] font-semibold">{b.key_benefit}</div>
                  </div>
                  <div className="col-span-12 md:col-span-3 flex md:flex-col items-center md:items-end gap-4">
                    <div className="text-left md:text-right">
                      <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C6470] font-semibold">Printed Book</div>
                      <div className="mt-1 flex md:justify-end items-baseline gap-2">
                        <span className="font-serif-d text-[30px] text-[#0D1B2A] leading-none">{b.print_price}</span>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row md:flex-col gap-2.5 w-full sm:w-auto md:items-end">
                      <Link
                        to={`/books/chinnu/${b.slug}`}
                        data-testid={`journey-view-${b.slug}`}
                        onClick={() => track("book_product_view", { product_id: b.product_id, source: "journey" })}
                        className="inline-flex items-center justify-center gap-2 border border-[#0D1B2A] text-[#0D1B2A] px-5 py-3 rounded-sm text-[12px] font-semibold uppercase tracking-wide hover:bg-[#0D1B2A] hover:text-white transition-colors duration-200"
                      >
                        Look Inside <ArrowRight size={14} />
                      </Link>
                      <Link
                        to={`/books/chinnu/checkout/${b.slug}`}
                        data-testid={`journey-buy-${b.slug}`}
                        onClick={() => track("chinnu_print_product_view", { product_id: b.print_product_id, source: "journey" })}
                        className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-sm text-[12px] font-semibold uppercase tracking-wide text-white hover:opacity-90 transition-opacity duration-200"
                        style={{ backgroundColor: "#F57F17" }}
                      >
                        Order — {b.print_price}
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLETE COLLECTION BANNER */}
      <section className="bg-white">
        <div className="max-w-[1240px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div data-testid="chinnu-bundle-card" className="relative bg-[#0D1B2A] text-white rounded-sm p-8 sm:p-12 lg:p-16 overflow-hidden">
              <span aria-hidden="true" className="font-telugu absolute -top-8 right-4 text-[12rem] leading-none text-outline-cream opacity-40 pointer-events-none select-none">పూర్తి</span>
              <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#F57F17]/15 blur-3xl pointer-events-none" />
              <div className="relative grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                <div className="md:col-span-7">
                  <div className="text-[12px] uppercase tracking-[0.24em] text-[#F57F17] font-semibold">Complete collection</div>
                  <h2 className="mt-4 font-serif-d text-[30px] sm:text-4xl lg:text-[44px] leading-[1.08] text-white">{CHINNU_BUNDLE.title}</h2>
                  <div className="mt-3 flex flex-wrap items-center gap-2.5">
                    <BilingualBadge dark testId="chinnu-series-bundle-bilingual-badge" />
                    <span className="text-[11px] uppercase tracking-[0.16em] font-bold text-white/75">4 Interactive Bilingual English–Telugu Books</span>
                  </div>
                  <p className="mt-5 text-[15px] sm:text-[17px] text-white/85 leading-[1.7] max-w-xl">{CHINNU_BUNDLE.description}</p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-white/70">
                    <span>Languages: {CHINNU_BUNDLE.languages}</span>
                    <span>Printed Book · 4 titles</span>
                  </div>
                  <div className="mt-7 flex flex-wrap items-end gap-4">
                    <div className="font-serif-d text-[46px] text-[#F57F17] leading-none">{CHINNU_BUNDLE.print_price}</div>
                    <div data-testid="bundle-savings-pill" className="pb-1.5 text-[12px] uppercase tracking-[0.14em] font-bold text-[#0D1B2A] bg-[#F5B25C] px-2.5 py-1 rounded-sm">Save {CHINNU_BUNDLE.print_savings}</div>
                  </div>
                  <p data-testid="chinnu-series-bundle-buy-context" className="mt-4 text-[12px] uppercase tracking-[0.14em] text-white/60 font-semibold">Bilingual English–Telugu · Printed Book</p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <Link
                      to="/books/chinnu/checkout/complete-collection"
                      data-testid="chinnu-bundle-buy-button"
                      onClick={() => track("chinnu_print_product_view", { product_id: CHINNU_BUNDLE.print_product_id, source: "series-bundle-banner" })}
                      className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-sm text-[13px] font-semibold uppercase tracking-wide text-white hover:opacity-90 transition-opacity duration-200"
                      style={{ backgroundColor: "#F57F17" }}
                    >
                      Get All 4 Printed Books — {CHINNU_BUNDLE.print_price}
                    </Link>
                    <Link
                      to="/books/chinnu/complete-collection"
                      data-testid="bundle-details-link"
                      onClick={() => track("bundle_view", { source: "series-banner" })}
                      className="inline-flex items-center justify-center gap-2 border border-white/40 text-white px-6 py-4 rounded-sm text-[13px] font-semibold uppercase tracking-wide hover:bg-white/10 transition-colors duration-200"
                    >
                      See What's Inside <ArrowRight size={14} />
                    </Link>
                  </div>
                </div>
                <div className="md:col-span-5 grid grid-cols-4 gap-2.5">
                  {CHINNU_BOOKS.map((b, i) => (
                    <div key={b.slug} style={{ transform: `translateY(${i % 2 ? "10px" : "0"})` }}>
                      <BookCover book={b} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* HOW PRINTED ORDERS WORK */}
      <section className="bg-[#F5F2EA]">
        <div className="max-w-[1100px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] text-[#004D40] font-semibold">How it works</div>
            <h2 className="mt-5 font-serif-d text-[32px] sm:text-4xl lg:text-[42px] leading-[1.08] text-[#0D1B2A]">From checkout to your doorstep.</h2>
          </Reveal>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-10">
            {[
              { n: "01", Icon: BookOpen, t: "Choose your book", d: "A single title, or the complete 4-book collection at a friendlier price." },
              { n: "02", Icon: CreditCard, t: "Pay securely", d: "Checkout is handled by Razorpay — India's trusted payment gateway." },
              { n: "03", Icon: Truck, t: "We ship your book", d: "Your printed copy is dispatched after payment is confirmed, to the address you provide at checkout." },
            ].map((s, i) => (
              <Reveal key={s.n} delay={i * 0.09}>
                <div data-testid={`how-it-works-step-${i + 1}`}>
                  <s.Icon size={22} className="text-[#F57F17] mb-2" />
                  <div className="font-serif-d text-[44px] text-[#F57F17] leading-none">{s.n}</div>
                  <div className="mt-4 font-serif-d text-[21px] text-[#0D1B2A]">{s.t}</div>
                  <p className="mt-2 text-[15px] text-[#5C6470] leading-[1.65]">{s.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-12 max-w-2xl text-[14px] text-[#5C6470] leading-[1.65]">
              Printed books are for personal and family use or gifting. Delivery is currently available within India only.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-24 md:py-32">
          <Reveal>
            <div className="text-[12px] uppercase tracking-[0.24em] text-[#004D40] font-semibold">Questions</div>
            <h2 className="mt-5 font-serif-d text-[32px] sm:text-4xl lg:text-[42px] leading-[1.08] text-[#0D1B2A]">Common questions about Chinnu.</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible data-testid="chinnu-faq-accordion" className="mt-12 border-t border-[#E5E7EA]">
              {CHINNU_FAQ.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-[#E5E7EA]">
                  <AccordionTrigger data-testid={`faq-item-${i}`} className="font-serif-d text-[18px] text-[#0D1B2A] hover:no-underline hover:text-[#D86B0F] py-5">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-[15px] text-[#5C6470] leading-[1.7]">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* ALREADY ORDERED? */}
      <section className="bg-[#FBF7EE] border-t border-[#E7DFCF]">
        <div className="max-w-[920px] mx-auto px-5 sm:px-8 py-10 text-center">
          <p className="text-[13px] text-[#5C6470]">
            Already ordered a printed Chinnu book?{" "}
            <Link to="/books/chinnu/track-order" data-testid="chinnu-track-order-link" className="font-semibold underline" style={{ color: "#F57F17" }}>
              Track your order →
            </Link>
          </p>
        </div>
      </section>
    </main>
  );
}
